import {useCallback, useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {authorizationHeaderFromAccessToken} from '../auth'
import {API_URL} from '../config'
import {captureException} from '../errors'
import ServerError from '../errors/server'
import {getAccessToken} from '../store/authentication/authentication.selectors'
import {AppState} from '../store/store'

export type SponsorType = 'NATIONAL' | 'COMMUNITY'

export interface Sponsor {
  id: string
  name: string
  type: SponsorType
  imageUrl?: string
}

type Options = Readonly<{
  onError?: (error: Error) => void
  onSuccess?: (sponsors: ReadonlyArray<Sponsor>) => void
}>

function useSponsors({onError, onSuccess}: Options = {}): {
  error: Error | null
  isLoading: boolean
  sponsors: ReadonlyArray<Sponsor> | undefined
  nationalSponsors: ReadonlyArray<Sponsor>
  communitySponsors: ReadonlyArray<Sponsor>
  refetch: () => void
} {
  const [sponsors, setSponsors] = useState<ReadonlyArray<Sponsor> | undefined>(
    undefined
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Safely get access token without throwing
  const accessToken = useSelector((state: AppState) => {
    try {
      return getAccessToken(state)
    } catch {
      return null
    }
  })

  const fetchSponsors = useCallback(() => {
    setError(null)
    setIsLoading(true)

    if (!accessToken) {
      const error = new Error('No access token available. Please log in.')
      setError(error)
      onError?.(error)
      setIsLoading(false)
      return
    }

    getSponsors(accessToken)
      .then(sponsors => {
        setSponsors(sponsors)
        onSuccess?.(sponsors)
      })
      .catch(error => {
        captureException(error)
        setError(error)
        onError?.(error)
      })
      .finally(() => setIsLoading(false))
  }, [accessToken, onError, onSuccess])

  useEffect(() => {
    fetchSponsors()
  }, [fetchSponsors])

  const nationalSponsors = sponsors?.filter(s => s.type === 'NATIONAL') ?? []
  const communitySponsors = sponsors?.filter(s => s.type === 'COMMUNITY') ?? []

  return {
    error,
    isLoading,
    sponsors,
    nationalSponsors,
    communitySponsors,
    refetch: fetchSponsors
  }
}

async function getSponsors(
  accessToken: string
): Promise<ReadonlyArray<Sponsor>> {
  const url = new URL('sponsors', API_URL)

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authorizationHeaderFromAccessToken(accessToken)
    }
  })

  if (response.status !== 200) {
    // Handle specific error cases
    if (response.status === 401) {
      throw new Error('Authentication failed. Please log in again.')
    }

    return ServerError.fromResponse(response)
  }
  return response.json()
}

export default useSponsors
